import { BalanceData, ChestBand, Rarity } from "../data/balance";
import { OfferItem } from "../data/offers";
import { Fix64 } from "../maths/fix64";

/**
 * Honest value badges (docs/SHOP.md §1.2; owner rule 2026-09-26): an offer's "value %" is computed from ONE documented
 * baseline, never written by hand. 100 % is the baseline gem pack's gems per whole price dollar (offers.json
 * value.baselinePack: 100 gems for $0.99). Every item has a gem price taken from what the game already charges:
 * supplies and speed-up minutes at the Shop's cheapest gem rate for them that every Commander can buy (shop.json
 * resourcePacks open from the start, speedUpPacks), gear
 * from the Black Market (offers.json value.gearGems, checked by gen.py), a chest as its parts, a lucky chest as its
 * odds-weighted chests, a skin at its gem band. The percent is rounded down.
 */

const PERCENT = 100;
const CENTS_PER_DOLLAR = 100;
const HALF = 2;

/** Gem prices of the item kinds (docs/SHOP.md §1.2). */
export interface GemPrices {
  /** One average Win chest of gold ("supplies"). */
  readonly supply: Fix64;
  readonly speedUpMinute: Fix64;
  /** Per rarity: one gear item. */
  readonly gear: readonly number[];
  /** Per rarity: one gear chest of that rarity (its resources, speed-up and mean gear count). */
  readonly chest: readonly Fix64[];
  /** Gems per whole dollar that count as 100 %. */
  readonly baselinePerDollar: Fix64;
}

/** The cheapest gems-per-unit in a list of (gems, units) prices, or zero when there is none. */
function cheapest(prices: readonly { gems: number; units: Fix64 }[]): Fix64 {
  let best: Fix64 | null = null;
  for (const p of prices) {
    if (p.units.isZero()) continue;
    const per = Fix64.fromInt(p.gems).div(p.units);
    if (best === null || per.lt(best)) best = per;
  }
  return best ?? Fix64.Zero;
}

/** The mean rarity multiplier of a Win chest: a chest's resources in "supplies" are its rarityMult over this. */
function meanWinMult(balance: BalanceData): Fix64 {
  const w = balance.chests.bandWeights[ChestBand.Win];
  let total = 0, sum = Fix64.Zero;
  w.forEach((x, r) => { total += x; sum = sum.add(balance.chests.rarityMult[r].mulInt(x)); });
  return total === 0 ? Fix64.One : sum.divInt(total);
}

export function gemPrices(balance: BalanceData): GemPrices {
  const shop = balance.commerce.shop, o = balance.offers, ch = balance.chests;
  // The rate every Commander can buy at: a pack that opens later (MP-07's War Coffers, level 10) is not the baseline.
  const supply = cheapest(shop.resourcePacks.filter((p) => p.unlockAfterLevel === 0).map((p) => ({ gems: p.gems, units: p.chests })));
  const speedUpMinute = cheapest(shop.speedUpPacks.map((p) => ({ gems: p.gems, units: Fix64.fromInt(p.minutes) })));
  const gear = o.value.gearGems;
  const mean = meanWinMult(balance);
  const chest = ch.rarityMult.map((m, r) => {
    const [lo, hi] = ch.gearCount[r] ?? [0, 0];
    return m.div(mean).mul(supply).add(speedUpMinute.mulInt(ch.speedUpMinutes[r] ?? 0)).add(Fix64.fromInt((gear[r] ?? 0) * (lo + hi)).divInt(HALF));
  });
  const base = shop.gemPacks.find((p) => p.id === o.value.baselinePack);
  const dollars = base ? Math.ceil(priceCentsOf(base.referencePrice) / CENTS_PER_DOLLAR) : 0;
  return { supply, speedUpMinute, gear, chest, baselinePerDollar: base && dollars > 0 ? Fix64.fromInt(base.gems).divInt(dollars) : Fix64.Zero };
}

function priceCentsOf(label: string): number {
  const m = /^\$(\d+)\.(\d\d)$/.exec(label);
  return m ? Number(m[1]) * CENTS_PER_DOLLAR + Number(m[2]) : 0;
}

/** The gem price of one offer item (all of its `count`). A skin the player owns is paid as gems instead (the same value). */
export function itemGems(balance: BalanceData, item: OfferItem, prices: GemPrices = gemPrices(balance)): Fix64 {
  switch (item.kind) {
    case "gems": return Fix64.fromInt(item.amount);
    case "supplies": return item.chests.mul(prices.supply);
    case "speedUp": return prices.speedUpMinute.mulInt(item.minutes * item.count);
    case "chest": return (prices.chest[item.rarity] ?? Fix64.Zero).mulInt(item.count);
    case "luckyChest": {
      const t = balance.offers.luckyChests.get(item.table);
      if (!t) return Fix64.Zero;
      let v = Fix64.Zero;
      t.weights.forEach((w, r) => { v = v.add((prices.chest[r] ?? Fix64.Zero).mulInt(w)); });
      return v.divInt(PERCENT).mulInt(item.count);
    }
    case "gear": return Fix64.fromInt((prices.gear[item.rarity] ?? 0) * item.count);
    case "skin": {
      const s = balance.commerce.shop.skins.list.find((k) => k.id === item.skinId);
      // a level or event skin has no gem price: it counts nothing; a season skin counts its gem-equivalent (repeatSkinGems)
      if (s?.unlock.by === "season") return Fix64.fromInt(balance.seasons.repeatSkinGems);
      // a Trophy Hall skin (MP-23) is priced in Trophies, not gems: never in an offer (gen.py), worth nothing here
      return Fix64.fromInt(s && "price" in s.unlock && s.unlock.by !== "trophies" ? s.unlock.price : 0);
    }
  }
}

export function itemsGems(balance: BalanceData, items: readonly OfferItem[], prices: GemPrices = gemPrices(balance)): Fix64 {
  return items.reduce((a, it) => a.add(itemGems(balance, it, prices)), Fix64.Zero);
}

/**
 * The value starburst: floor(100 × the items' gem value / (baseline gems per dollar × the price in whole dollars,
 * rounded up)). 0 when there is no baseline. For an offer with picks or a prize the caller passes the items as they
 * stand (a pick not yet made counts its cheapest choice: `cheapestChoice`).
 */
export function valuePercent(balance: BalanceData, items: readonly OfferItem[], priceCents: number, prices: GemPrices = gemPrices(balance)): number {
  const dollars = Math.ceil(priceCents / CENTS_PER_DOLLAR);
  if (dollars <= 0 || prices.baselinePerDollar.isZero()) return 0;
  return itemsGems(balance, items, prices).mulInt(PERCENT).div(prices.baselinePerDollar.mulInt(dollars)).floorToInt();
}

/**
 * "Worth N gems" (MP-09; a page with valueStyle "worth"): the same computed gem value the percent is made of, rounded
 * down, stated plainly on a recurring deal instead of a burst.
 */
export function worthGems(balance: BalanceData, items: readonly OfferItem[], prices: GemPrices = gemPrices(balance)): number {
  return itemsGems(balance, items, prices).floorToInt();
}

/** Whether a value starburst is worth showing (offers.json value.showFromPercent). */
export function showsValue(balance: BalanceData, percent: number): boolean { return percent >= balance.offers.value.showFromPercent; }

/** The cheapest of a pick list (what an unfilled pick or prize counts for in the value). */
export function cheapestChoice(balance: BalanceData, choices: readonly OfferItem[], prices: GemPrices = gemPrices(balance)): OfferItem | null {
  let best: OfferItem | null = null, bestGems: Fix64 | null = null;
  for (const c of choices) {
    const g = itemGems(balance, c, prices);
    if (bestGems === null || g.lt(bestGems)) { best = c; bestGems = g; }
  }
  return best;
}

/** Items whose contents are rolled after paying (a gear chest's gear, a lucky chest's rarity, a gear slot 'any'). */
export function isRandomItem(item: OfferItem): boolean {
  return item.kind === "chest" || item.kind === "luckyChest" || (item.kind === "gear" && item.slot === "any");
}

export function hasRandomItem(items: readonly OfferItem[]): boolean { return items.some(isRandomItem); }

/** What the odds sheet shows for a random item (Apple 3.1.1 disclosure before purchase). */
export interface ItemOdds {
  /** Percent per rarity (index = Rarity) of the chest itself; 100 on one rarity for a fixed-rarity chest. */
  readonly rarityPercent: readonly number[];
  /** Per rarity: [min, max] gear items the chest holds (chests.json gearCount). */
  readonly gearCount: readonly (readonly [number, number])[];
  /** Gear slots an item may land on (uniform): 1 / this each. */
  readonly slots: number;
  /** An item's level requirement is the chest's level minus 0..this. */
  readonly levelReqSpread: number;
  /** Per rarity: [min, max] speed-up minutes / resources are fixed by rarity and level (shown as numbers by the caller). */
  readonly speedUpMinutes: readonly number[];
}

/** The odds of a random item; null for an item that is not random. */
export function itemOdds(balance: BalanceData, item: OfferItem): ItemOdds | null {
  if (!isRandomItem(item)) return null;
  const n = balance.chests.rarityMult.length;
  let rarityPercent: number[];
  if (item.kind === "chest") rarityPercent = Array.from({ length: n }, (_, r) => (r === item.rarity ? PERCENT : 0));
  else if (item.kind === "luckyChest") rarityPercent = [...(balance.offers.luckyChests.get(item.table)?.weights ?? [])];
  else rarityPercent = Array.from({ length: n }, (_, r) => (r === (item as { rarity: Rarity }).rarity ? PERCENT : 0));
  return { rarityPercent, gearCount: balance.chests.gearCount, slots: balance.gear.slots.length, levelReqSpread: balance.gear.itemLevel.dropSpread, speedUpMinutes: balance.chests.speedUpMinutes };
}
